import { StateCreator } from "zustand";
import { State } from "./types";
import { fetchClientAvatar, initialState, TEMPLATE_CODE } from "./utils";
import {
	ClientEntityType,
	PractitionerEntityType,
	SupabaseRpcEnum,
	SupabaseTableEnum,
} from "../../../../lib/supabase/supabaseTypes";
import { supabase } from "../../../../lib/supabase";
import { Logger } from "../../../../lib/logger/Logger";
import { usePatientStore } from "../Patient";
import { showNotification } from "./selectors";
import { produce } from "immer";
import axios from "axios";
import { useJobStore } from "../Jobs";
import {
	ConnectionRequest,
	MessageType,
} from "@/lib/store/Realtime/message.types";

export interface ConnectGetOrCreateUserResponse {
	data: {
		data: {
			profileId: string | null;
			organizationId: string | null;
		};
		message: string;
	};
	error: string | null;
}

export interface ClientSlice extends State {
	switchClient: (clientId: string) => Promise<void>;
	getPractitioners: (clientId: string) => Promise<void>;
	createPractitioner: (
		practitioner: PractitionerEntityType,
		jobId?: number
	) => Promise<void>;
	updatePractitioner: (practitioner: PractitionerEntityType) => Promise<void>;
	selectPractitioner: (
		jobId: number,
		practitionerId: number
	) => Promise<void>;
	getClients: () => Promise<void>;
	upsertClient: (client: ClientEntityType) => Promise<void>;
	favoriteClient: (clientId: string, favorite: boolean) => Promise<void>;
	connectClientFromScratch: (
		adminEmail: string,
		message: string,
		clientType: "med" | "lab",
		onSuccess?: () => void
	) => Promise<void>;
	connectClientToAlreadyConnectedOrg: (
		connectedOrganizationId: string
	) => Promise<boolean>;
	connectClientToExistingVendorConnectRelationship: (
		vendorOrgId: string,
		message: string
	) => Promise<boolean>;
	disconnectClient: () => Promise<void>;
	setConnectedOrganizationEmail: (email: string | null) => void;
}

export const createClientStore: StateCreator<ClientSlice> = (set, get) => ({
	...initialState,
	setConnectedOrganizationEmail: (email: string | null) => {
		set({ connectedOrganizationEmail: email });
	},
	switchClient: async (clientId: string) => {
		set({ clientLoading: true });
		const clients = get().clientsLookup;
		if (!clients || !clients[clientId]) {
			set({ client: null, clientLoading: false });
			Logger.warn("No clients found or clientId does not exist");
			return;
		}
		const client = clients[clientId];
		const connectedOrganizationId = client.connected_organization_id;

		let connectedOrganizationEmail = null;
		// Fetch the email of the connected organization
		if (connectedOrganizationId) {
			const { data, error } = await supabase
				.from(SupabaseTableEnum.PROFILES)
				.select("email")
				.eq("organization_id", connectedOrganizationId)
				.single();

			if (data) {
				connectedOrganizationEmail = data.email;
			}
		}

		set({
			clientId,
			client,
			clientLoading: false,
			clientOrganizationId: connectedOrganizationId,
			connectedOrganizationEmail: connectedOrganizationEmail,
		});

		// Fetch the patients for the client
		await usePatientStore.getState().fetchPatients(clientId);
		await get().getPractitioners(clientId);
	},
	getPractitioners: async (clientId: string) => {
		const { data, error } = await supabase
			.from(SupabaseTableEnum.PRACTITIONERS)
			.select("*")
			.eq("client_id", clientId);

		if (error) {
			Logger.warn("Error fetching practitioners", error);
		}

		const newPractitionersLookup = data?.reduce(
			(acc: Record<string, PractitionerEntityType>, practitioner) => {
				acc[practitioner.id] = practitioner;
				return acc;
			},
			{}
		);

		// Practitioners are fetched for each client, so we need to keep the existing practitioners from 
		// earlier clients
		set((state) => ({
			practitionersLookup: {
				...state.practitionersLookup,
				...newPractitionersLookup,
			},
		}));
	},
	createPractitioner: async (
		practitioner: PractitionerEntityType,
		jobId?: number
	) => {
		if (!practitioner) {
			showNotification({
				message: "Behandler nicht gefunden.",
				type: "error",
			});
			return;
		}
		const { data, error } = await supabase
			.from(SupabaseTableEnum.PRACTITIONERS)
			.insert({
				title: practitioner.title,
				first_name: practitioner.first_name,
				last_name: practitioner.last_name,
				email: practitioner.email,
				client_id: practitioner.client_id,
			})
			.select()
			.single();

		if (error) {
			showNotification({
				message: "Fehler beim Erstellen des Behandlers.",
				type: "error",
			});
			return;
		}

		set((state) => ({
			practitionersLookup: {
				...state.practitionersLookup,
				[data.id]: data,
			},
		}));

		if (jobId) {
			await get().selectPractitioner(jobId, data.id);
		}

		showNotification({
			message: "Behandler erfolgreich erstellt.",
			type: "success",
		});
	},
	updatePractitioner: async (practitioner: PractitionerEntityType) => {
		Logger.log("updatePractitioner", practitioner);
		if (!practitioner || !practitioner.id || !practitioner.client_id) {
			showNotification({
				message: "Behandler nicht gefunden.",
				type: "error",
			});
			return;
		}
		set((state) => ({
			practitionersLookup: {
				...state.practitionersLookup,
				[practitioner.id]: practitioner,
			},
		}));
		const { data, error } = await supabase
			.from(SupabaseTableEnum.PRACTITIONERS)
			.update({
				title: practitioner.title,
				first_name: practitioner.first_name,
				last_name: practitioner.last_name,
				email: practitioner.email,
			})
			.eq("id", practitioner.id)
			.select()
			.single();

		if (error) {
			showNotification({
				message: "Fehler beim Aktualisieren des Behandlers.",
				type: "error",
			});
			get().getPractitioners(practitioner.client_id);
			return;
		}
	},
	selectPractitioner: async (jobId: number, practitionerId: number) => {
		if (!practitionerId) {
			showNotification({
				message: "Behandler nicht gefunden.",
				type: "error",
			});
			return;
		}
		if (!jobId) {
			showNotification({
				message: "Auftrag nicht gefunden.",
				type: "error",
			});
			return;
		}

		const { data, error } = await supabase
			.from(SupabaseTableEnum.JOBS)
			.update({ practitioner_id: practitionerId })
			.eq("id", jobId);

		if (error) {
			showNotification({
				message: "Fehler beim Auswählen des Behandlers.",
				type: "error",
			});
			return;
		}

		await useJobStore.getState().fetchJob(jobId);
	},
	getClients: async () => {
		set({ clientsLoading: true });

		const organizationId = get().organization?.id;

		if (!organizationId) {
			Logger.warn("Organization id is not set");
			set({ clientsLoading: false });
			return;
		}

		const { data, error } = await supabase
			.from(SupabaseTableEnum.CLIENTS)
			.select()
			.eq("organization_id", organizationId as string)
			.neq("code", TEMPLATE_CODE) // TEMPLATE_CODE is the code of the dummy client for templates
			.order("created_at", { ascending: false });

		if (error) {
			set({ clientsLoading: false });
			Logger.warn(error);
			return;
		}

		if (!data) {
			set({ clientsLoading: false });
			return;
		}

		const newClientsLookup = data?.reduce(
			(
				acc: Record<string, ClientEntityType>,
				client: ClientEntityType
			) => {
				acc[client.id] = client;
				return acc;
			},
			{}
		);

		// Set the clientsLookup instead of waiting for the avatars to be fetched
		set({
			clientsLookup: newClientsLookup,
			clientsLoading: false,
		});

		const createClientsLookupWithAvatars = async () => {
			const newClientsLookup: Record<
				string,
				ClientEntityType & { image_src?: string }
			> = {};

			for (const client of data) {
				if (client.avatar_path) {
					const image_src = await fetchClientAvatar(
						client.avatar_path
					);
					// @ts-expect-error this is a new field to store the
					// image url
					if (image_src) client.image_src = image_src;
				}

				newClientsLookup[client.id] = client;
			}
		};

		// This will update the clients avatar path without having the user wait for the avatars to be fetched
		// Beauty of asynchrony
		createClientsLookupWithAvatars();
	},
	upsertClient: async (client: ClientEntityType & { image_src?: string }) => {
		if (client.avatar_path) {
			const image_src = await fetchClientAvatar(client.avatar_path);

			if (image_src) client.image_src = image_src;
		}
		set((state) => ({
			clientsLookup: {
				...state.clientsLookup,
				[client.id]: client,
			},
			client,
			clientId: client.id,
			clientOrganizationId: client.connected_organization_id,
		}));

		// eslint-disable-next-line @typescript-eslint/naming-convention
		const { image_src, search_vector, ...rest } = client as any;

		await supabase.from(SupabaseTableEnum.CLIENTS).upsert(rest, {
			onConflict: "id",
		});
	},
	favoriteClient: async (clientId: string, favorite: boolean) => {
		const newValue = favorite ? new Date().toISOString() : null;

		const { error } = await supabase
			.from(SupabaseTableEnum.CLIENTS)
			.update({
				favorite: newValue,
			})
			.eq("id", clientId);

		if (error) {
			showNotification({
				message: "Fehler beim Favorisieren des Kunden",
				type: "error",
			});
			Logger.warn(error);
			return;
		}

		set(
			produce((state) => {
				state.clientsLookup[clientId].favorite = newValue;
				// Only update the client if it is the currently selected client
				if (state.client && state.client.id === clientId) {
					state.client.favorite = newValue;
				}
			})
		);
	},
	connectClientToAlreadyConnectedOrg: async (
		connectedOrganizationId: string
	) => {
		const clientId = get().clientId;

		if (!connectedOrganizationId) {
			showNotification({
				message: "Verbindung nicht gefunden.",
				type: "error",
			});
			return false;
		}

		if (!clientId) {
			showNotification({
				message: "Kunde nicht gefunden.",
				type: "error",
			});
			return false;
		}

		const { error } = await supabase
			.from(SupabaseTableEnum.CLIENTS)
			.update({
				connected_organization_id: connectedOrganizationId,
			})
			.eq("id", clientId);

		if (error) {
			showNotification({
				message:
					"Fehler beim Verbinden des Kunden mit einem anderen Auftraggeber",
				type: "error",
			});
			Logger.error(error);
			return false;
		}

		await get().getClients();
		window.location.reload();
		return true;
	},
	connectClientToExistingVendorConnectRelationship: async (
		vendorOrgId: string,
		message: string
	) => {
		const clientId = get().clientId;
		const organizationId = get().organization?.id;

		if (!organizationId) {
			showNotification({
				message: "Organisation nicht gefunden.",
				type: "error",
			});
			return false;
		}

		if (!clientId) {
			showNotification({
				message: "Kunde nicht gefunden.",
				type: "error",
			});
			return false;
		}

		if (!vendorOrgId) {
			showNotification({
				message: "Bestehender Lieferant nicht gefunden.",
				type: "error",
			});
			return false;
		}

		const messageContent: ConnectionRequest = {
			type: MessageType.CONNECTION_REQUEST,
			message: message,
			isRequestByVendor: true,
			clientType: "lab",
		};

		const { error } = await supabase.rpc(SupabaseRpcEnum.CONNECT_CLIENT, {
			p_client_id: clientId,
			p_vendor_org_id: organizationId,
			p_customer_org_id: vendorOrgId, // in this case the vendor will be the customer (switcheroo)
			p_message_content: messageContent,
		});

		if (error) {
			showNotification({
				message:
					"Fehler beim Verbinden des Kunden mit einem bestehenden Lieferanten",
				type: "error",
			});
			Logger.error(error);
			return false;
		}

		await get().getClients();
		window.location.reload();
		return true;
	},
	connectClientFromScratch: async (
		adminEmail: string,
		message: string,
		clientType: "med" | "lab",
		onSuccess?: () => void
	) => {
		const organizationId = get().organization?.id;
		const clientId = get().clientId;
		if (!organizationId) {
			showNotification({
				message: "Organisation nicht gefunden.",
				type: "error",
			});
			return;
		}
		if (!clientId) {
			showNotification({
				message: "Kunde nicht gefunden.",
				type: "error",
			});
			return;
		}
		if (!adminEmail) {
			showNotification({
				message: "Kein Admin-Email angegeben.",
				type: "error",
			});
			return;
		}

		let medOrgId: string | null = null;

		// The backend checks if a user/profile with the given email exists
		// If not, it signs up a new user (with a new organization) and returns profile_id and organization_id
		// If yes, it retrieves profile_id and organization_id
		try {
			const {
				data,
				error: connectError,
			}: ConnectGetOrCreateUserResponse = await axios({
				url: `${import.meta.env.VITE_PDF_MICROSERVICE_URL
					}/connect/get-or-create-user`,
				method: "POST",
				data: {
					email: adminEmail.toLowerCase(),
					org_type: clientType,
				},
			});

			if (connectError) {
				showNotification({
					message: "Fehler beim Verbinden des Behandlers (A2).",
					type: "error",
				});
				return;
			}

			medOrgId = data.data.organizationId;
		} catch (error) {
			showNotification({
				message: "Fehler beim Verbinden des Behandlers (A1).",
				description: `${adminEmail} ${error}`,
				type: "error",
			});
			return;
		}
		if (!medOrgId) {
			showNotification({
				message: "Fehler beim Verbinden des Behandlers (A4).",
				type: "error",
			});
			return;
		}

		const messageContent: ConnectionRequest = {
			type: MessageType.CONNECTION_REQUEST,
			message: message,
			isRequestByVendor: true,
			clientType: clientType,
		};

		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.CONNECT_CLIENT,
			{
				p_client_id: clientId,
				p_vendor_org_id: organizationId,
				p_customer_org_id: medOrgId,
				p_message_content: messageContent,
			}
		);

		if (error) {
			Logger.error(error, {}, "connectPractitioner");
			showNotification({
				message: "Fehler beim Verbinden des Behandlers (A5).",
				type: "error",
			});
			return;
		}

		// Check if the RPC returned data (it returns an array of results)
		if (!data || !Array.isArray(data) || data.length === 0) {
			showNotification({
				message: "Fehler beim Verbinden des Behandlers (A7).",
				type: "error",
			});
			return;
		}

		const result = data[0];

		// Check for existing connection
		if (!result.success && result.message === "exists") {
			showNotification({
				message:
					"Zu dieser E-Mail besteht bereits eine Verbindung. Nutzen Sie eine der anderen beiden Optionen.",
				type: "warning",
			});
			return;
		}

		// Check for other errors
		if (!result.success) {
			showNotification({
				message: "Fehler beim Verbinden des Behandlers.",
				description: result.message,
				type: "error",
			});
			return;
		}

		const currentClientId = get().clientId;
		const currentClient = get().client;

		if (
			currentClientId &&
			currentClient
		) {
			const updatedClient = {
				...currentClient,
				connected_organization_id: medOrgId,
			};

			set((state) => ({
				clientsLookup: {
					...state.clientsLookup,
					[currentClientId]: updatedClient,
				},
				client: updatedClient,
				clientOrganizationId: medOrgId,
				connectedOrganizationEmail: adminEmail,
			}));

			if (onSuccess) {
				onSuccess();
			}

			showNotification({
				message: "Verbindung erfolgreich hergestellt",
				type: "success",
			});
		} else {
			showNotification({
				message: "Fehler beim Aktualisieren des Clients.",
				type: "error",
			});
			window.location.reload();
		}
	},
	/**
	 * This function removes the connect relationship only from the current client
	 * The relationship entry remains
	 */
	disconnectClient: async () => {
		const clientId = get().clientId;
		if (!clientId) {
			showNotification({
				message: "Auftraggeber nicht gefunden.",
				type: "error",
			});
			return;
		}

		const { error } = await supabase
			.from(SupabaseTableEnum.CLIENTS)
			.update({
				connected_organization_id: null,
			})
			.eq("id", clientId);

		if (error) {
			showNotification({
				message: "Fehler beim Trennen der Verbindung.",
				type: "error",
			});
			return;
		}

		window.location.reload();
	},
});
